import { useEffect, useState } from 'react'
import { Html, OrbitControls } from '@react-three/drei'
import { ATTRACTOR, attractors } from './attractors'
import { Particles } from './Particles'
import { Trails } from './Trails'

const pages = [
  { id: 'lab', label: 'Lab' },
  { id: 'about', label: 'About' },
  { id: 'contact', label: 'Contact' }
]

const pageAttractors = {
  home: ATTRACTOR.FOUR_WING,
  lab: ATTRACTOR.AIZAWA,
  about: ATTRACTOR.THOMAS,
  contact: ATTRACTOR.LORENZ
}

function getPageFromHash() {
  const page = window.location.hash.slice(1).toLowerCase()
  return pages.some(({ id }) => id === page) ? page : 'home'
}

function PageContent({ page }) {
  if (page === 'lab') {
    return (
      <>
        <p className="page-eyebrow">Lab</p>
        <h1>Strange Attractors.</h1>
        <p className="page-intro">
          Interactive studies in motion, particles, shaders, and strange attractors.
        </p>
        <p className="page-hint">Choose a strange attractor below.</p>
      </>
    )
  }

  if (page === 'about') {
    return (
      <>
        <p className="page-eyebrow">About · Berlin</p>
        <h1>Simulation, systems, and texture.</h1>
        <p className="page-intro">
          I&apos;m Tolga, a computational physics M.Sc. student at Freie Universität Berlin. My research
          focuses on GPU-accelerated molecular-dynamics simulations of confined fluids and amorphous
          surfaces.
        </p>
        <p className="page-copy">
          Alongside my research, I work as an Associate Data Engineer at Seeing Machines Germany.
          I also create 3D and real-time visual work with Blender and TouchDesigner.
        </p>
        <ul className="topic-list" aria-label="Areas of interest">
          <li>Molecular dynamics</li>
          <li>GPU computing</li>
          <li>Data engineering</li>
          <li>Real-time visuals</li>
        </ul>
      </>
    )
  }

  if (page === 'contact') {
    return (
      <>
        <p className="page-eyebrow">Contact</p>
        <h1>Say hi.</h1>
        <p className="page-intro">
          Research, engineering, creative technology, or anything else—feel free to send me an
          email.
        </p>
        <div className="contact-links">
          <a href="mailto:tolga.atalay@fu-berlin.de">
            <span>Email</span>
            <strong>tolga.atalay@fu-berlin.de</strong>
            <span aria-hidden="true">↗</span>
          </a>
          <a href="https://github.com/atalaytolga" target="_blank" rel="noreferrer">
            <span>GitHub</span>
            <strong>@atalaytolga</strong>
            <span aria-hidden="true">↗</span>
          </a>
        </div>
        <p className="contact-location">Based in Berlin, Germany.</p>
      </>
    )
  }

  return (
    <>
      <p className="page-eyebrow">Personal website</p>
      <h1>Tolga Atalay</h1>
    </>
  )
}

export default function App() {
  const [page, setPage] = useState(getPageFromHash)
  const [attractor, setAttractor] = useState(
    () => pageAttractors[getPageFromHash()] ?? ATTRACTOR.FOUR_WING
  )
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    function handleHashChange() {
      const nextPage = getPageFromHash()
      setPage(nextPage)
      setMenuOpen(false)
      setAttractor(pageAttractors[nextPage])
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [])

  useEffect(() => {
    document.title = page === 'home'
      ? 'Tolga Atalay'
      : `${pages.find(({ id }) => id === page)?.label} — Tolga Atalay`
  }, [page])

  useEffect(() => {
    function handleKeyDown(event) {
      if (page !== 'lab') return

      const selectedAttractor = attractors.find(
        ({ shortcut }) => shortcut === event.key
      )

      if (selectedAttractor) {
        setAttractor(selectedAttractor.id)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [page])

  return (
    <>
      <OrbitControls
        makeDefault
        autoRotate
        autoRotateSpeed={0.5}
        minDistance={2.5}
        maxDistance={7}
        zoomSpeed={0.1}
      />

      <Html fullscreen style={{ pointerEvents: 'none' }}>
        <div className="site-interface">
          <header className="site-header">
            <a className="site-brand" href="#home" aria-label="Tolga Atalay, home">
              Home
            </a>

            <button
              type="button"
              className="menu-toggle"
              aria-expanded={menuOpen}
              aria-controls="site-navigation"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? 'Close' : 'Menu'}
            </button>

            <nav
              id="site-navigation"
              className={`site-navigation${menuOpen ? ' is-open' : ''}`}
              aria-label="Primary navigation"
            >
              {pages.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  aria-current={page === id ? 'page' : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </a>
              ))}
            </nav>
          </header>

          <main className="page-panel" key={page}>
            <PageContent page={page} />
          </main>

          {page === 'lab' && (
            <nav className="attractor-switcher" aria-label="Choose attractor">
              {attractors.map(({ id, label, shortcut }) => (
                <button
                  key={id}
                  type="button"
                  aria-label={`${label} attractor, keyboard shortcut ${shortcut}`}
                  aria-pressed={attractor === id}
                  className={attractor === id ? 'is-active' : ''}
                  onClick={() => setAttractor(id)}
                >
                  {label}
                </button>
              ))}
            </nav>
          )}
        </div>
      </Html>

      <Particles
        attractor={attractor}
        focus={4.25}
        aperture={4}
        fov={12}
      />
      {/* <Trails retention={0.965} />*/}
    </>
  )
}
